import { CheckboxWithField } from 'components/Checkbox';
import FormRow from 'components/FormRow';
import FormSection from 'components/FormSection';
import MarkdownWrapper from 'components/MarkdownWrapper/MarkdownWrapper';
import { validateSectionFields } from 'modules/Inquiry/Form/formConditions';
import {
  CONTACT_PERSON_ADVERTISING_CONSENT,
  CONTACT_PERSON_AFFILIATE_ADVERTISING_CONSENT,
  CONTACT_PERSON_BUSINESS_PARTNER_ADVERTISING_CONSENT,
  CONTACT_PERSON_SECURITY_CONSENT,
} from 'modules/Inquiry/Form/formFields';
import { useFieldValidators } from 'shared/hooks/useFieldValidators';
import { useTranslations } from 'utils/hooks/useTranslations';
import { combineValidators } from 'utils/validators';

type Props = {
  sectionNumber: number;
};

// EBS-40 / EBS-41: the BfS consent block is a single mandatory data-protection acknowledgement
// (→ privacy-policy-accepted) followed by three optional marketing consents — SozialBank,
// its sister companies, and the cooperation partner (Compeon) — plus a revocation notice.
export const validatebfsConsentSection = () =>
  validateSectionFields([CONTACT_PERSON_SECURITY_CONSENT]);

export const BfsTermsAndConditions = ({ sectionNumber }: Props) => {
  const { required, mustBeTrue } = useFieldValidators();
  const t = useTranslations('pages.companyDetails.sections.termsAndConditions.bfs');

  return (
    <FormSection sectionNumber={sectionNumber} title={t('title')}>
      {/* Mandatory data-protection acknowledgement → privacy-policy-accepted. */}
      <FormRow>
        <CheckboxWithField
          name={CONTACT_PERSON_SECURITY_CONSENT}
          validate={combineValidators(required, mustBeTrue)}
          text={
            <span>
              {t('fields.contactPersonSecurityConsent.label', {
                dataSecurity: (
                  <a href={t('fields.contactPersonSecurityConsent.links.dataSecurity.href')}>
                    {t('fields.contactPersonSecurityConsent.links.dataSecurity.text')}
                  </a>
                ),
              })}
            </span>
          }
        />
      </FormRow>
      {/* Heading for the optional marketing consents. */}
      <FormRow>
        <MarkdownWrapper text={t('fields.advertisingConsentHeading.label')} />
      </FormRow>
      {/* Optional consent to SozialBank marketing communications. */}
      <FormRow>
        <CheckboxWithField
          name={CONTACT_PERSON_ADVERTISING_CONSENT}
          text={<span>{t('fields.contactPersonAdvertisingConsent.label')}</span>}
          optional
        />
      </FormRow>
      {/* Optional consent to share data with the sister companies. */}
      <FormRow>
        <CheckboxWithField
          name={CONTACT_PERSON_AFFILIATE_ADVERTISING_CONSENT}
          text={<span>{t('fields.contactPersonAffiliateAdvertisingConsent.label')}</span>}
          optional
        />
      </FormRow>
      {/* Optional consent to be contacted by the cooperation partner (Compeon). */}
      <FormRow>
        <CheckboxWithField
          name={CONTACT_PERSON_BUSINESS_PARTNER_ADVERTISING_CONSENT}
          text={<span>{t('fields.contactPersonBusinessPartnerAdvertisingConsent.label')}</span>}
          optional
        />
      </FormRow>
      {/* Revocation notice shown below the marketing consents. */}
      <FormRow>
        <MarkdownWrapper text={t('fields.revocationNotice.label')} />
      </FormRow>
      {/* Mandatory-fields footnote. */}
      <FormRow>
        <MarkdownWrapper text={t('fields.mandatoryFieldsNote.label')} />
      </FormRow>
    </FormSection>
  );
};
